import { useCallback, useEffect } from 'react'
import { useListReportRequestsLazyQuery, ReportRequestStateEnum } from '__codegen__/api'
import { edgesToCollection } from 'legacy/apollo/utils/edgesToCollection'
import { useRetailerId } from 'utils/routing/params.hooks'
import { type ReportRequestListItem } from 'legacy/sections/generated-reports/utils/generatedReports.types'
import { listReportRequestsHandleStopPolling } from 'legacy/sections/generated-reports/graphql/list.utils'

const REPORT_WAITING_STATES = [ReportRequestStateEnum.InProgress, ReportRequestStateEnum.New]

export const useListReportRequests = () => {
  const retailerId = useRetailerId()

  const [loadReportsRequests, { data, error, loading, called, stopPolling }] =
    useListReportRequestsLazyQuery({
      pollInterval: 5000,
      fetchPolicy: 'no-cache',
    })

  const shouldStopPolling =
    data &&
    !data.reportRequests.edges.some(({ node: { generationState } }) =>
      REPORT_WAITING_STATES.includes(generationState)
    )

  useEffect(() => {
    if (shouldStopPolling && stopPolling) {
      listReportRequestsHandleStopPolling(stopPolling)
    }
  }, [shouldStopPolling, stopPolling])

  const fetchReportRequests = useCallback(
    ({ limit, offset }) => loadReportsRequests({ variables: { retailerId, limit, offset } }),
    [loadReportsRequests, retailerId]
  )

  const totalCount = (data && data.reportRequests.meta.totalCount) || 0
  const edges = (data && data.reportRequests.edges) || []
  const reportRequests: Array<ReportRequestListItem> = edgesToCollection(edges)

  return {
    loading,
    error,
    called,
    reportRequests,
    totalCount,
    fetchReportRequests,
  }
}

export type UseListReportRequestsPayload = ReturnType<typeof useListReportRequests>
